import { addSegmentHeader } from '../global/tracking/segment-endpoints';
let lb4ApiUrl = null;
let lb2ApiUrl = null;
let accessTokenId = null;
let signOut = null;
/** When true, backend must not log client IP (patient context). Set by Angular app. */
let getIsPatientContext = null;
export const setUpApi = (config) => {
    lb4ApiUrl = `${config.lb4ApiUrl}/api`;
    lb2ApiUrl = `${config.apiUrl}/api`;
    signOut = config.signOut;
    accessTokenId = config.accessTokenId;
};
/**
 * Register getter for patient context. When it returns true, API requests
 * send X-Doxyme-User-Role: patient so the backend does not log client IP.
 */
export function setPatientContextGetter(getter) {
    getIsPatientContext = getter;
}
/** Header to add when in patient context (backend must not log IP). */
const patientRoleHeaders = () => getIsPatientContext?.() === true ? { 'X-Doxyme-User-Role': 'patient' } : {};
class ApiError extends Error {
    constructor(message, data) {
        super(message);
        this.data = data;
    }
}
export function generateUrl(url, baseUrl, queryParams) {
    const endpointUrl = new URL(`${baseUrl}${url}`);
    if (queryParams) {
        for (const key in queryParams) {
            endpointUrl.searchParams.append(key, queryParams[key]);
        }
    }
    return endpointUrl.toString();
}
export async function createRequest({ baseUrl, endpoint, method = 'GET', data, enableAutoLogout = true, queryParams, }) {
    const overrideSecret = localStorage.getItem('overrideSecret');
    const overrideIP = localStorage.getItem('overrideIP');
    const settings = {
        headers: {
            authorization: accessTokenId,
            ...patientRoleHeaders(),
            ...(overrideSecret && overrideIP && {
                'x-override-secret': overrideSecret,
                'x-override-ip': overrideIP,
            }),
        },
        body: undefined,
        method,
    };
    if (data !== undefined && ['POST', 'PUT', 'DELETE'].includes(method)) {
        settings.headers['Content-Type'] = 'application/json';
        settings.body = JSON.stringify(data);
    }
    const endpointUrl = generateUrl(endpoint, baseUrl, queryParams);
    const response = await fetch(endpointUrl, settings);
    if (!response.ok) {
        if (response.status === 401 || (response.status === 403 && enableAutoLogout)) {
            void signOut();
            throw new Error('Unauthorized');
        }
        const { error } = await response.json();
        throw new ApiError(error.message, error.data);
    }
    if (response.status !== 204) {
        return (await response.json());
    }
    return null;
}
export const createExtensionRequest = async (params) => createRequest({ ...params, baseUrl: lb4ApiUrl });
export const API = {
    get accessTokenId() {
        return accessTokenId;
    },
    meetingHistory: {
        async getMeetingHistoryByParams({ userIds, roomIds, institutionId, locationIds, offset, limit, start = new Date(0), end = new Date(), }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/sessions/getMeetingHistory`,
                method: 'POST',
                data: {
                    start,
                    userIds,
                    roomIds,
                    locationIds,
                    end,
                    institutionId,
                    offset,
                    limit,
                },
            });
        },
        async getMeetingHistoryForCSVByParams({ userIds, roomIds, locationIds, institutionId, start = new Date(0), end = new Date(), }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/sessions/getMeetingHistoryForCSV`,
                method: 'POST',
                data: {
                    start,
                    userIds,
                    roomIds,
                    locationIds,
                    end,
                    institutionId,
                },
            });
        },
    },
    analytics: {
        async getAnalytics({ userIds, roomIds, institutionId, locationIds, }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/getAnalytics`,
                method: 'POST',
                data: {
                    institutionId,
                    userIds,
                    roomIds,
                    locationIds,
                },
            });
        },
        async getAnalyticsOverview({ institutionId, userIds, roomIds, locationIds, start = new Date('1970-01-01T00:00:00'), end = new Date(), }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/getAnalyticsOverview`,
                method: 'POST',
                data: {
                    institutionId,
                    userIds,
                    roomIds,
                    locationIds,
                    start,
                    end,
                },
            });
        },
    },
    extensions: {
        async getTeleconsent() {
            return createExtensionRequest({
                endpoint: `/extensions/teleconsent`,
            });
        },
        async getExtensionById(queryParams, extensionId) {
            return createExtensionRequest({
                endpoint: `/extensions/${extensionId}`,
                queryParams,
            });
        },
        async getAll(queryParams) {
            return createExtensionRequest({
                endpoint: '/extensions/install/all',
                queryParams,
            });
        },
        async getInstalled(queryParams) {
            return createExtensionRequest({
                endpoint: '/extensions/install/installed',
                queryParams,
            });
        },
        async getUninstalled(queryParams) {
            return createExtensionRequest({
                endpoint: '/extensions/install/uninstalled',
                queryParams,
            });
        },
        async install(queryParams, extensionId) {
            return createExtensionRequest({
                endpoint: `/extensions/install/${extensionId}`,
                queryParams,
                method: 'POST',
            });
        },
        async uninstall(queryParams, extensionId) {
            return createExtensionRequest({
                endpoint: `/extensions/uninstall/${extensionId}`,
                queryParams,
                method: 'POST',
            });
        },
        async awareness(queryParams, extensionId) {
            return createExtensionRequest({
                endpoint: `/extensions/${extensionId}/awareness`,
                queryParams,
                method: 'POST',
            });
        },
        // Need to moved from extensions to institution
        // This method can update institution and institutionAccess
        async updatePublicAttributes(institutionAccess, institutionId, institution) {
            const data = {
                ...institutionAccess,
            };
            if (institution) {
                data.attributes = institution;
            }
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/updatePublicAttributes`,
                data,
                method: 'PUT',
            });
        },
    },
    extensionsAdmin: {
        async getAll() {
            return createExtensionRequest({
                endpoint: '/extensions/admin/extensions',
            });
        },
        async getExtensionById(extensionId) {
            return createExtensionRequest({
                endpoint: `/extensions/admin/extensions/${extensionId}`,
            });
        },
        async updateExtension(extension) {
            return createExtensionRequest({
                endpoint: `/extensions/admin/extensions`,
                method: 'PUT',
                data: extension,
            });
        },
        async createExtension(extension) {
            return createExtensionRequest({
                endpoint: `/extensions/admin/extensions`,
                method: 'POST',
                data: extension,
            });
        },
        async deleteExtension(extension) {
            return createExtensionRequest({
                endpoint: `/extensions/admin/extensions`,
                method: 'DELETE',
                data: extension,
            });
        },
        async getFileStatistic(data) {
            const overrideSecret = localStorage.getItem('overrideSecret');
            const overrideIP = localStorage.getItem('overrideIP');
            const settings = {
                headers: {
                    authorization: accessTokenId,
                    ...patientRoleHeaders(),
                    ...(overrideSecret && overrideIP && {
                        'x-override-secret': overrideSecret,
                        'x-override-ip': overrideIP,
                    }),
                },
                method: 'GET',
            };
            const { extensionId, ...queryParams } = data;
            const endpointUrl = generateUrl(`/extensions/admin/extensions/${extensionId}/usage.csv`, lb4ApiUrl, queryParams);
            return fetch(endpointUrl, settings);
        },
        async publish(queryParams, extensionId) {
            return createExtensionRequest({
                endpoint: `/extensions/publish/${extensionId}`,
                queryParams,
                method: 'PATCH',
            });
        },
        async unpublish(queryParams, extensionId) {
            return createExtensionRequest({
                endpoint: `/extensions/unpublish/${extensionId}`,
                queryParams,
                method: 'PATCH',
            });
        },
        async getStatisticsByParams(data) {
            return createExtensionRequest({
                endpoint: `/extensions/admin/extensions/${data.extensionId}/usage`,
                queryParams: data,
                method: 'GET',
            });
        },
    },
    privateStorages: {
        async uploadFile({ formData, token, temporary = true }) {
            const overrideSecret = localStorage.getItem('overrideSecret');
            const overrideIP = localStorage.getItem('overrideIP');
            const settings = {
                headers: {
                    authorization: accessTokenId,
                    ...patientRoleHeaders(),
                    ...(overrideSecret && overrideIP && {
                        'x-override-secret': overrideSecret,
                        'x-override-ip': overrideIP,
                    }),
                },
                method: 'POST',
                body: formData,
            };
            const endpointUrl = generateUrl('/privateStorages/uploadFile', lb2ApiUrl, {
                token,
                temporary,
            });
            return fetch(endpointUrl, settings).then(async (response) => response.json());
        },
    },
    betaFeatures: {
        async createBetaFeature(data) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/betaFeatures/createBetaFeature`,
                method: 'POST',
                data,
            });
        },
        async softDeleteBetaFeature(queryParams) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/betaFeatures/softDeleteBetaFeature`,
                method: 'PATCH',
                queryParams,
            });
        },
    },
    betaAccess: {
        async createBetaAccess(data) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/betaAccesses`,
                method: 'POST',
                data,
            });
        },
        async removeFeatureForUsers(data) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/betaAccesses/removeFeatureForUsers`,
                method: 'POST',
                data,
            });
        },
        async removeFeatureForInstitutions(data) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/betaAccesses/removeFeatureForInstitutions`,
                method: 'POST',
                data,
            });
        },
        async getBetaAccesses({ institutionId, userId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/betaAccesses/getBetaAccesses`,
                method: 'GET',
                queryParams: {
                    institutionId,
                    userId,
                },
            });
        },
    },
    institution: {
        async getAnalyticsFilterOptions({ institutionId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/getAnalyticsFilterOptions`,
                method: 'GET',
            });
        },
        async getInstitutionsList({ filter = null }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions`,
                queryParams: filter,
                method: 'GET',
            });
        },
        async addUsersBatch({ institutionId, data }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/addUsersBatch`,
                method: 'POST',
                data,
            });
        },
        async bulkUsersUpdate(data) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutionLocation/bulkUsersUpdate`,
                method: 'POST',
                data,
            });
        },
        async uniqueEmailForInstitution(payload) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/uniqueEmailForInstitution`,
                queryParams: payload,
                method: 'GET',
            });
        },
        async getInstitutionLocations({ institutionId, limit, offset, sortBy, isDesc, }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutionLocation`,
                method: 'GET',
                queryParams: {
                    institutionId,
                    limit,
                    offset,
                    sortBy,
                    isDesc,
                },
            });
        },
        async getLocationAccess({ institutionId, locationId, }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutionLocation/${locationId}/access`,
                method: 'GET',
                queryParams: {
                    institutionId,
                },
            });
        },
        async deleteInstitutionLocations(locationId) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutionLocation/${locationId}`,
                method: 'DELETE'
            });
        },
        async createInstitutionLocations({ name, institutionId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutionLocation`,
                method: 'POST',
                data: {
                    name,
                    institutionId
                },
            });
        },
        async editInstitutionLocations({ name, locationId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutionLocation/${locationId}`,
                method: 'POST',
                data: {
                    name,
                }
            });
        },
        async getInstitutionSharedRooms({ institutionId, query, limit = 10, offset = 0, sortBy, isDesc, }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/getSharedRoomsForClinicSettings`,
                method: 'GET',
                queryParams: {
                    query,
                    limit,
                    offset,
                    sortBy,
                    isDesc,
                },
            });
        },
        async getInstitutionSharedRoomsCount({ institutionId, query }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/getSharedRoomsForClinicSettings/count`,
                method: 'GET',
                queryParams: {
                    query,
                },
            });
        },
        async getUsersForRoomAccess({ institutionId, query, limit = 10, offset = 0, sortBy, isDesc, }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/getUsersForRoomAccess`,
                method: 'GET',
                queryParams: {
                    query,
                    limit,
                    offset,
                    sortBy,
                    isDesc,
                },
            });
        },
        async updateInstitutionMembership({ institutionId, institutionMemberId, props }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/institutionMemberships/${institutionMemberId}`,
                data: props,
                method: 'PUT',
            });
        },
        async countRoomAccessUsers({ institutionId, query }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/countRoomAccessUsers`,
                method: 'GET',
                queryParams: {
                    query,
                },
            });
        },
        async getUsersForManageUsers({ institutionId, query = '', limit = 10, offset = 0, sortBy = null, isDesc = null, }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/manageUsers`,
                method: 'GET',
                queryParams: {
                    query,
                    limit,
                    offset,
                    sortBy,
                    isDesc,
                },
            });
        },
        async revokeInviteToken({ institutionId, id }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/inviteTokens/${id}`,
                method: 'DELETE',
            });
        },
        async deleteUser({ institutionId, id }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/users/rel/${id}`,
                method: 'DELETE',
            });
        },
        async resendInvite({ institutionId, tokenId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/resendInvite`,
                data: {
                    tokenId,
                },
                method: 'POST',
            });
        },
        async exportUserList({ institutionId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/exportUserList`,
                method: 'GET',
            });
        },
        async getUsersForManageUsersCount({ institutionId, query = '' }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/manageUsers/count`,
                method: 'GET',
                queryParams: {
                    query,
                },
            });
        },
        async updateInstitution(institution, institutionId) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}`,
                data: institution,
                method: 'PUT',
            });
        },
        async updateBranding(branding, institutionId) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/branding`,
                data: branding,
                method: 'PUT',
            });
        },
        async findRooms({ institutionId, searchValue, limit = 10, offset = 0, sortBy, isDesc, isPersonal = true }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/findRooms`,
                method: 'GET',
                queryParams: {
                    searchValue,
                    limit,
                    offset,
                    sortBy,
                    isDesc,
                    isPersonal,
                },
            });
        },
        async findRoomsCount({ institutionId, searchValue = '', isPersonal = true }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/institutions/${institutionId}/findRooms/count`,
                method: 'GET',
                queryParams: {
                    searchValue,
                    isPersonal,
                },
            });
        },
        async bulkSetRoomWidgets({ locationId, roomId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/roomWidgets/bulkSetRoomWidgets`,
                data: {
                    locationId,
                    roomId,
                },
                method: 'POST',
            });
        },
        async restoreDefaultRoomWidgets({ locationId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/roomWidgets/restoreDefaultRoomWidgets`,
                data: {
                    locationId
                },
                method: 'POST',
            });
        },
    },
    user: {
        async createCurrentPatientToken() {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/users/createCurrentPatientToken',
                method: 'POST',
            });
        },
        async generateOAuthStripeToken({ id }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/generateOAuthStripeToken`,
                data: { id },
                method: 'POST',
            });
        },
        async updateUser({ id, payload }) {
            const overrideSecret = localStorage.getItem('overrideSecret');
            const overrideIP = localStorage.getItem('overrideIP');
            const endpointUrl = generateUrl(`/users/${id}`, lb2ApiUrl);
            const headers = {
                authorization: accessTokenId,
                'Content-Type': 'application/json',
                ...patientRoleHeaders(),
                ...(overrideSecret && overrideIP && {
                    'x-override-secret': overrideSecret,
                    'x-override-ip': overrideIP,
                }),
            };
            await addSegmentHeader(headers);
            const settings = {
                headers,
                method: 'PUT',
                body: JSON.stringify(payload),
            };
            try {
                const response = await fetch(endpointUrl, settings);
                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        void signOut();
                        throw new Error('Unauthorized');
                    }
                    const { error } = await response.json();
                    throw new ApiError(error.message, error.data);
                }
                if (response.status !== 204) {
                    return (await response.json());
                }
                return null;
            }
            catch (error) {
                if (error instanceof TypeError && error.message === 'Failed to fetch') {
                    throw new ApiError('Network error: Failed to fetch', {});
                }
                // Re-throw ApiError and other errors as-is
                throw error;
            }
        },
        async getLinkedin({ id, code, redirectUri }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/getLinkedin`,
                data: {
                    code,
                    redirectUri,
                },
                method: 'POST',
            });
        },
        async locationsLicenced({ id, locations }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/locationsLicenced`,
                data: locations,
                method: 'POST',
            });
        },
        async updateLegalsById({ id, fk, legalProps }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/legals/${fk}`,
                data: legalProps,
                method: 'PUT',
            });
        },
        async isValidPassword({ id, payload }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/isValidPassword`,
                data: payload,
                method: 'POST',
            });
        },
        async unique(payload) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/unique`,
                queryParams: payload,
                method: 'GET',
            });
        },
        async deleteUser({ id, payload }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/deleteUser`,
                data: payload,
                method: 'POST',
            });
        },
        async getCharges({ id }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/charges`,
                method: 'GET',
            });
        },
        async previewNextPayment({ id }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/previewNextPayment`,
                method: 'GET',
            });
        },
        async purchases({ id, filter }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/purchases`,
                method: 'GET',
                queryParams: filter,
            });
        },
        async validateCaptcha(data) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/validateCaptcha`,
                method: 'POST',
                data,
            });
        },
        async getUserDataByInviteToken(token) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/inviteTokens/findByToken`,
                method: 'GET',
                queryParams: { token },
            });
        },
        async addStripeSource(id, data) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/sources`,
                method: 'POST',
                data,
            });
        },
        async getLegals({ id, filter }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/legals`,
                method: 'GET',
                queryParams: { filter: JSON.stringify(filter) },
            });
        },
        async createRoom({ id, data }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${id}/rooms`,
                method: 'POST',
                data,
            });
        },
        async getAvailableRooms({ userId, institutionId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/users/${userId}/availableRooms`,
                method: 'GET',
                queryParams: {
                    institutionId,
                },
            });
        },
        async getSupportedCurrency() {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/stripe/currencies`,
                method: 'GET',
            });
        },
    },
    legal: {
        async deleteBaa({ id }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/legals/${id}/deleteBaa`,
                method: 'POST',
            });
        },
        async createBaa(payload) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/legals/createBaa`,
                method: 'POST',
                data: payload,
            });
        },
    },
    userGroups: {
        async getByUser({ institutionId, userId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/userGroups`,
                method: 'GET',
                queryParams: {
                    institutionId,
                    userId,
                },
            });
        },
        async addToUser({ institutionId, userId, groupId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/userGroups/add`,
                method: 'POST',
                data: {
                    institutionId,
                    userId,
                    groupId,
                },
            });
        },
        async removeFromUser({ institutionId, userId, groupId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/userGroups/remove`,
                method: 'POST',
                data: {
                    institutionId,
                    userId,
                    groupId,
                },
            });
        },
    },
    roomGroups: {
        async getByRoom({ institutionId, roomId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/roomGroups`,
                method: 'GET',
                queryParams: {
                    institutionId,
                    roomId,
                },
            });
        },
        async addToRoom({ institutionId, roomId, groupId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/roomGroups/add`,
                method: 'POST',
                data: {
                    institutionId,
                    roomId,
                    groupId,
                },
            });
        },
        async removeFromRoom({ institutionId, roomId, groupId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/roomGroups/remove`,
                method: 'POST',
                data: {
                    institutionId,
                    roomId,
                    groupId,
                },
            });
        },
    },
    document: {
        async getTemporaryUrl({ id, download }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/documents/${id}/temporaryUrl`,
                method: 'GET',
                queryParams: { download },
            });
        },
    },
    storages: {
        async upload({ storage, formData, imageType }) {
            const overrideSecret = localStorage.getItem('overrideSecret');
            const overrideIP = localStorage.getItem('overrideIP');
            const settings = {
                headers: {
                    authorization: accessTokenId,
                    ...patientRoleHeaders(),
                    ...(overrideSecret && overrideIP && {
                        'x-override-secret': overrideSecret,
                        'x-override-ip': overrideIP,
                    }),
                },
                method: 'POST',
                body: formData,
            };
            const endpointUrl = generateUrl(`/Storages/${storage}/upload${imageType ? `?imageType=${imageType}` : ''}`, lb2ApiUrl, {});
            return fetch(endpointUrl, settings).then(async (response) => response.json());
        },
    },
    room: {
        async findRoomById({ id }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/findRoomById/${id}`,
                method: 'GET',
            });
        },
        async update({ id, props }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${id}`,
                data: props,
                method: 'PUT',
            });
        },
        async findBySlug({ slug, queryParams = {} }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/slug/${slug}`,
                queryParams,
                method: 'GET',
            });
        },
        async addRoomMember({ roomId, userId, institutionId, role }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${roomId}/roomMemberships`,
                method: 'POST',
                data: {
                    userId,
                    institutionId,
                    role,
                },
            });
        },
        async removeRoomMember({ roomId, roomMemberId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${roomId}/roomMemberships/${roomMemberId}`,
                method: 'DELETE',
            });
        },
        async inviteBySms(data) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${data.id}/inviteBySms`,
                method: 'POST',
                data,
            });
        },
        async inviteByEmail({ roomId, email, language, edited, message, calendarICS }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${roomId}/inviteByEmail`,
                method: 'POST',
                data: {
                    email,
                    language,
                    edited,
                    message,
                    calendarICS,
                },
            });
        },
        async deleteRoom({ roomId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${roomId}`,
                method: 'DELETE',
            });
        },
        async isUnique(queryParams) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/isUnique`,
                queryParams,
                method: 'GET',
            });
        },
        async usersOfSharedRoomsForSettings({ roomId, ...queryParams }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${roomId}/sharedUsers`,
                queryParams,
                method: 'GET',
            });
        },
        async addAllInstitutionMembersToRoom(roomId) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${roomId}/addAllInstitutionMembers`,
                method: 'POST',
            });
        },
        async removeAllMembersFromRoom(roomId) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${roomId}/removeAllMembers`,
                method: 'POST',
            });
        },
        async allInstitutionMembersBelongToRoom(roomId) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/rooms/${roomId}/allInstitutionMembersBelong`,
                method: 'GET',
            });
        },
        async onPatientLeft({ roomId, name, pid }) {
            if (typeof navigator.sendBeacon === 'function') {
                const params = { name };
                if (pid !== undefined) {
                    params.pid = pid;
                }
                const data = new URLSearchParams(params);
                return navigator.sendBeacon(`${lb2ApiUrl}/rooms/${roomId}/onPatientLeft`, data);
            }
            else {
                return createRequest({
                    baseUrl: lb2ApiUrl,
                    endpoint: `/rooms/${roomId}/onPatientLeft`,
                    method: 'POST',
                    data: { name, pid },
                });
            }
        },
        async startLiveCaptions({ sessionId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/rooms/startLiveCaptions',
                method: 'POST',
                data: {
                    sessionId,
                },
            });
        },
        async stopLiveCaptions({ captionsId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/rooms/stopLiveCaptions',
                method: 'POST',
                data: {
                    captionsId,
                },
            });
        },
        async createSession({ participants, roomId, mediaMode }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/rooms/createSession',
                method: 'POST',
                data: {
                    participants,
                    roomId,
                    mediaMode,
                },
            });
        },
        async joinSession({ ownershipToken, participants }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/rooms/joinSession',
                method: 'POST',
                data: {
                    ownershipToken,
                    participants,
                },
            });
        },
    },
    purchase: {
        async preview({ id, payload }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/purchases/${id}/preview`,
                data: payload,
                method: 'POST',
            });
        },
        async previewCostPerSeat({ id, payload }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/purchases/${id}/previewCostPerSeat`,
                data: payload,
                method: 'POST',
            });
        },
        async validateCoupon(payload) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/purchases/validateCoupon`,
                method: 'POST',
                data: payload,
            });
        }
    },
    stripe: {
        async createCustomerPortalSession() {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/stripe/customer-portal',
                method: 'GET',
            });
        },
        async getSource(sourceId, customerId) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/stripe/get-source/${sourceId}`,
                method: 'POST',
                data: {
                    customerId,
                },
            });
        },
        async createSubscription({ email, countryCode, planId, postalCode, idempotencyKey, seats, coupon, quotedPrice }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/stripe/subscription-create',
                method: 'POST',
                data: {
                    email,
                    countryCode,
                    planId,
                    postalCode,
                    idempotencyKey,
                    seats,
                    coupon,
                    quotedPrice,
                },
            });
        },
        async createUserAccount({ signupFormData, subscription, customer, paymentMethod, }) {
            const endpointUrl = generateUrl('/stripe/user-account-create', lb2ApiUrl);
            const headers = {
                authorization: accessTokenId,
                'Content-Type': 'application/json',
                ...patientRoleHeaders(),
            };
            await addSegmentHeader(headers);
            const settings = {
                headers,
                method: 'POST',
                body: JSON.stringify({
                    userData: signupFormData,
                    subscription,
                    customer,
                    paymentMethod,
                }),
            };
            try {
                const response = await fetch(endpointUrl, settings);
                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        void signOut();
                        throw new Error('Unauthorized');
                    }
                    const { error } = await response.json();
                    throw new ApiError(error.message, error.data);
                }
                return (await response.json());
            }
            catch (error) {
                if (error instanceof TypeError && error.message === 'Failed to fetch') {
                    throw new ApiError('Network error: Failed to fetch', {});
                }
                // Re-throw ApiError and other errors as-is
                throw error;
            }
        },
        async getTaxAmount({ email, planId, postalCode, countryCode, seats, coupon }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/stripe/get-tax',
                method: 'POST',
                data: {
                    email,
                    planId,
                    postalCode,
                    countryCode,
                    seats,
                    coupon,
                },
            });
        },
        async updateCustomerCountryAndZip({ customerId, email, planId, options }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/stripe/update-customer-country-zip',
                method: 'POST',
                data: {
                    customerId,
                    email,
                    planId,
                    options,
                },
            });
        },
        async getCustomerPaymentMethod({ customerId, paymentMethodId }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/stripe/get-customer-payment-method',
                method: 'POST',
                data: {
                    customerId,
                    paymentMethodId,
                },
            });
        }
    },
    callRating: {
        async save({ sessionId, rating, data, comment, noSoundDetected, isProviderFeedback, surveyType, vendor }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/callRatings/save',
                method: 'POST',
                data: {
                    sessionId,
                    rating,
                    data,
                    comment,
                    noSoundDetected,
                    isProviderFeedback,
                    surveyType,
                    vendor
                },
            });
        },
    },
    dokbotWorkflow: {
        async getWorkflow({ userId, institutionId }) {
            const queryParams = {};
            if (userId !== undefined) {
                queryParams.userId = userId;
            }
            if (institutionId !== undefined) {
                queryParams.institutionId = institutionId;
            }
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/dokbotWorkflow/getWorkflow',
                method: 'GET',
                queryParams,
            });
        },
        async updateWorkflow({ institutionId = null, userId = null, patientSurveyId, providerSurveyId, patientSurveyFrequency, providerSurveyFrequency }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/dokbotWorkflow/updateWorkflow`,
                method: 'POST',
                queryParams: {},
                data: {
                    institutionId,
                    userId,
                    patientSurveyId,
                    providerSurveyId,
                    patientSurveyFrequency,
                    providerSurveyFrequency,
                }
            });
        },
    },
    userManageableFeatures: {
        async getFeatures({ userId, institutionId }) {
            const queryParams = {
                userId
            };
            if (Boolean(institutionId)) {
                queryParams.institutionId = institutionId;
            }
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: '/userManageableFeatures',
                method: 'GET',
                queryParams
            });
        },
        async setFeatureValue({ userId, institutionId, featureKey, isEnabled }) {
            return createRequest({
                baseUrl: lb2ApiUrl,
                endpoint: `/userManageableFeatures/${featureKey}`,
                method: 'PUT',
                queryParams: {},
                data: {
                    userId,
                    institutionId,
                    isEnabled,
                }
            });
        }
    },
    account: {
        async clinicSignup({ user, institution, payment, recaptchaToken }) {
            const endpointUrl = generateUrl('/account/clinic/signup', lb2ApiUrl);
            const headers = {
                authorization: accessTokenId,
                'Content-Type': 'application/json',
                ...patientRoleHeaders(),
            };
            await addSegmentHeader(headers);
            const settings = {
                headers,
                method: 'POST',
                body: JSON.stringify({
                    user,
                    institution,
                    payment,
                    recaptchaToken,
                }),
            };
            try {
                const response = await fetch(endpointUrl, settings);
                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        void signOut();
                        throw new Error('Unauthorized');
                    }
                    const { error } = await response.json();
                    throw new ApiError(error.message, error.data);
                }
                return (await response.json());
            }
            catch (error) {
                if (error instanceof TypeError && error.message === 'Failed to fetch') {
                    throw new ApiError('Network error: Failed to fetch', {});
                }
                // Re-throw ApiError and other errors as-is
                throw error;
            }
        }
    }
};
